/**
 * Recovery from stale-build chunk errors.
 *
 * After a deploy the old hashed chunks are gone from the server, so a tab that
 * still runs the previous index.html fails to import them. A single full reload
 * fetches the new index.html and fixes it. The guard below keeps that to ONE
 * reload per time window: the timestamp is NOT cleared on page load (that is
 * what made the previous counter useless), it simply expires.
 */

export const CHUNK_RELOAD_KEY = 'hlb_chunk_reload_at';
export const CHUNK_RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module', // Chromium
  'importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox
  'unable to preload css', // Vite CSS preload
  'loading chunk', // webpack-style "Loading chunk N failed"
  'loading css chunk',
];

/**
 * @param {unknown} error - Error, rejection reason, route error or vite:preloadError event.
 * @returns {boolean} true when the error means a code chunk could not be loaded.
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  const candidate = error.payload ?? error.error ?? error.reason ?? error;

  if (error.type === 'vite:preloadError') return true;
  if (candidate?.name === 'ChunkLoadError') return true;

  const message = String(
    (typeof candidate === 'string' ? candidate : candidate?.message) ??
      error.message ??
      ''
  ).toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

let reloadPending = false;

/** Test helper: forget the in-memory "reload already requested" flag. */
export function resetChunkReloadStateForTests() {
  reloadPending = false;
}

function readLastReload() {
  try {
    return Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeLastReload(timestamp) {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(timestamp));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads the page once for a chunk error.
 *
 * @param {() => void} [reload] - Injected for tests; defaults to location.reload.
 * @returns {boolean} true when a reload was triggered, false when the guard
 *   blocked it (a reload already happened within the window) — the caller
 *   should then show an error message instead.
 */
export function reloadOnceForChunkError(
  reload = () => window.location.reload()
) {
  // Several listeners (vite:preloadError, route errorElement, ...) can report
  // the same failure; once this page has asked for a reload, just wait for it.
  if (reloadPending) return true;

  const now = Date.now();

  if (now - readLastReload() < CHUNK_RELOAD_WINDOW_MS) {
    console.error(
      'Chunk load error persists after reload; not reloading again.'
    );
    return false;
  }

  // Without storage the guard cannot work, so do not risk a reload loop.
  if (!writeLastReload(now)) return false;

  console.warn(
    'Chunk load error detected (new version deployed?), reloading page once...'
  );
  reloadPending = true;
  reload();
  return true;
}
